import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, Copy, Gift } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2000;

export const SUCCESS_TEST_IDS = {
  voucherCode: "gift-success-voucher-code",
  copyBtn: "gift-success-copy-btn",
  status: "gift-success-status",
};

const CadeaubonSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", data: null, message: "Betaling wordt gecontroleerd..." });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null, message: "Ongeldige sessie." });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/gift-vouchers/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid" && data.voucher_code) {
          setState({ status: "paid", data, message: "Betaling gelukt!" });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data, message: "Sessie is verlopen." });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({
            status: "timeout",
            data,
            message: "Duurt langer dan verwacht — check je e-mail of contacteer ons.",
          });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          data: null,
          message: err?.response?.data?.detail || "Fout bij ophalen van betaling.",
        });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const copy = async () => {
    if (!state.data?.voucher_code) return;
    try {
      await navigator.clipboard.writeText(state.data.voucher_code);
      toast.success("Code gekopieerd.");
    } catch {
      toast.error("Kopiëren mislukt.");
    }
  };

  const isPaid = state.status === "paid";

  return (
    <section className="pt-40 md:pt-48 pb-24 bg-white min-h-[80vh]">
      <div className="container-narrow max-w-2xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          {isPaid ? (
            <>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border border-gold text-gold mb-8">
                <Check size={32} strokeWidth={1.5} />
              </div>
              <p className="eyebrow mb-3">Betaling bevestigd</p>
              <h1 className="font-serif text-5xl md:text-6xl leading-[1.05]">
                Bedankt<span className="italic text-gold">.</span>
              </h1>
              {state.data.recipient_name && (
                <p className="mt-6 text-neutral-600">
                  Jouw cadeau voor{" "}
                  <span className="font-serif italic">{state.data.recipient_name}</span> ligt klaar.
                </p>
              )}

              <div className="mt-12 bg-beige p-10 md:p-12">
                <p className="eyebrow mb-4">Voucher code</p>
                <p
                  data-testid={SUCCESS_TEST_IDS.voucherCode}
                  className="font-serif text-4xl md:text-5xl tracking-[0.15em] text-ink"
                >
                  {state.data.voucher_code}
                </p>
                <p className="mt-4 text-2xl font-serif text-gold">
                  Waarde: €{state.data.amount_total.toFixed(0)}
                </p>
                <button
                  data-testid={SUCCESS_TEST_IDS.copyBtn}
                  onClick={copy}
                  className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-neutral-600 hover:text-gold transition"
                >
                  <Copy size={14} /> Kopieer code
                </button>
              </div>

              <p className="mt-10 text-sm text-neutral-600 leading-relaxed">
                Bewaar deze code zorgvuldig. Hij is inwisselbaar bij het boeken van een fotoshoot.
                Contacteer ons op{" "}
                <a href={`mailto:${BRAND.email}`} className="text-gold underline">
                  {BRAND.email}
                </a>{" "}
                om hem in te wisselen.
              </p>

              <p className="mt-4 text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                Bij vragen over overschrijving:&nbsp;<span className="text-neutral-500 normal-case tracking-normal">{BRAND.iban}</span>
              </p>

              <div className="mt-12 flex flex-wrap justify-center gap-4">
                <Link to="/" className="btn-outline-dark">
                  Terug naar home
                </Link>
                <Link to="/contact" className="btn-gold">
                  Plan de shoot
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-gold text-gold mb-8">
                <Gift size={26} strokeWidth={1.4} />
              </div>
              <h1 className="font-serif text-4xl md:text-5xl leading-[1.1]">
                {state.status === "error" || state.status === "expired"
                  ? "Er ging iets mis"
                  : "Bevestigen..."}
              </h1>
              <p data-testid={SUCCESS_TEST_IDS.status} className="mt-6 text-neutral-600">
                {state.message}
              </p>
              {(state.status === "error" || state.status === "expired" || state.status === "timeout") && (
                <Link to="/cadeaubonnen" className="btn-outline-dark mt-10">
                  Terug naar cadeaubonnen
                </Link>
              )}
              {state.status === "loading" && (
                <div className="mt-8 inline-block w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
              )}
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default CadeaubonSuccess;
